// purpose: title view (opening-screen-sheet MENU LAYOUT + mode-select-parts-sheet
// cards 01/02/03). Presentation only — the runtime call for BEGIN RESTORATION is
// owned by App so the departure transition and the double-click guard live in
// one place.
import { memo } from 'react';
import { DifficultyId } from '../../game/constants';
import { DIFFICULTIES } from '../../game/data/difficulty';
import type { GameRuntime } from '../../game/runtime/GameRuntime';

/** Card accent key → the `--accent-*` ramp picked in styles.css. */
const ACCENT: Record<number, string> = {
  [DifficultyId.Calm]: 'calm',
  [DifficultyId.Bright]: 'bright',
  [DifficultyId.Fracture]: 'fracture',
};

const CARD_NOTE: Record<number, string> = {
  [DifficultyId.Calm]: 'WIDE TELEGRAPHS',
  [DifficultyId.Bright]: 'REFERENCE SIGNAL',
  [DifficultyId.Fracture]: 'DENSE FRACTURE',
};

interface TitlePanelProps {
  runtime: GameRuntime;
  difficulty: DifficultyId;
  departing: boolean;
  onSelectDifficulty: (id: DifficultyId) => void;
  onBegin: () => void;
  onOpenSettings: () => void;
}

/** Fix 3(b): all callback props are already stable (setState or useCallback in App). */
export const TitlePanel = memo(function TitlePanel({ runtime, difficulty, departing, onSelectDifficulty, onBegin, onOpenSettings }: TitlePanelProps) {
  return (
    <section className={departing ? 'title-panel departing' : 'title-panel'} aria-labelledby="game-title">
      <div className="title-drift" aria-hidden="true"><i /><i /><i /></div>

      <div className="title-lockup">
        <p className="eyebrow">REPAIR THE VANISHING ROUTES</p>
        <h1 id="game-title"><span>LUMINA</span><b>ARCHIVE</b></h1>
        <div className="title-divider" aria-hidden="true" />
        <p className="title-copy">A MEMORY WORLD SUSPENDED IN GLASS, FLOWING WITH LIGHT.</p>
      </div>

      <fieldset className="difficulty-picker">
        <legend>DIFFICULTY</legend>
        {DIFFICULTIES.map((item) => (
          <button
            data-menu-item
            data-accent={ACCENT[item.id]}
            key={item.id}
            type="button"
            className={difficulty === item.id ? 'difficulty active' : 'difficulty'}
            aria-pressed={difficulty === item.id}
            onClick={() => onSelectDifficulty(item.id)}
          >
            <strong>{item.name}</strong>
            <i className="card-crystal" aria-hidden="true" />
            <span>{CARD_NOTE[item.id]}</span>
            <i className="card-frame" aria-hidden="true"><b /><b /><b /><b /></i>
          </button>
        ))}
      </fieldset>

      <div className="title-actions">
        <span className="menu-slot wide">
          <button data-menu-item type="button" className="button primary" onClick={onBegin}>BEGIN RESTORATION</button>
        </span>
        <span className="menu-slot">
          <button data-menu-item type="button" className="button" onClick={() => runtime.openControls()}>CONTROLS</button>
        </span>
        <span className="menu-slot">
          <button data-menu-item type="button" className="button" onClick={onOpenSettings}>SETTINGS</button>
        </span>
      </div>

      <p className="input-hint">
        <span>TOUCH TO BEGIN <em>or PRESS</em> <b aria-hidden="true">A</b></span>
        <small>KEYBOARD · GAMEPAD · DIRECT TOUCH</small>
      </p>
    </section>
  );
});
